import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FileText, Upload, Trash2, Download, Star, Loader2, CheckCircle2, Sparkles } from "lucide-react";

export default function CVs() {
  const [cvs, setCvs] = useState([]);
  const [tailored, setTailored] = useState([]);
  const [tailoredSort, setTailoredSort] = useState("recent");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/cvs"), api.get("/tailored-cvs")]);
    setCvs(c.data);
    setTailored(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) { toast.error("Provide a title and a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("file", file);
      await api.post("/cvs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CV uploaded");
      setTitle(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/cvs/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleBase = async (cv) => {
    await api.put(`/cvs/${cv.id}`, { is_base_cv: !cv.is_base_cv });
    toast.success(cv.is_base_cv ? "Unset base CV" : "Set as base CV");
    load();
  };

  const delTailored = async (id) => {
    await api.delete(`/tailored-cvs/${id}`);
    toast.success("Tailored CV deleted");
    load();
  };

  const sortedTailored = [...tailored].sort((a, b) => {
    if (tailoredSort === "match") return (b.alignment?.score ?? -1) - (a.alignment?.score ?? -1);
    if (tailoredSort === "company") return (a.company || "").localeCompare(b.company || "");
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="space-y-8" data-testid="cvs-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">CVs</h1>
        <p className="text-muted-foreground mt-1 text-sm">Upload your base CVs. Mark one as default for tailoring.</p>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader><CardTitle className="font-heading text-xl">Upload a CV</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Base CV – 2026" data-testid="cv-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">File (PDF / DOC / DOCX)</Label>
              <Input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cv-file-input" className="cursor-pointer" />
            </div>
            <Button type="submit" disabled={uploading} data-testid="upload-cv-button" className="active:scale-[0.98] transition-transform">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Upload</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cvs.length === 0 && <p className="text-sm text-muted-foreground">No CVs uploaded yet.</p>}
        {cvs.map((cv) => (
          <Card key={cv.id} className="border-zinc-200 shadow-sm" data-testid={`cv-card-${cv.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{cv.title}</p>
                    {cv.is_base_cv && <Badge className="bg-green-100 text-green-800 hover:bg-green-100 gap-1"><CheckCircle2 className="h-3 w-3" /> Base</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{cv.file_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{new Date(cv.created_at).toLocaleDateString()} · {cv.file_type.toUpperCase()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <a href={`${API}/cvs/${cv.id}/download`} target="_blank" rel="noreferrer" className="flex-1" data-testid={`download-cv-${cv.id}`}>
                  <Button variant="outline" size="sm" className="w-full active:scale-[0.98] transition-transform">
                    <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> View
                  </Button>
                </a>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => toggleBase(cv)} data-testid={`base-cv-${cv.id}`} title="Set as base CV">
                  <Star className={`h-4 w-4 ${cv.is_base_cv ? "fill-yellow-400 text-yellow-500" : ""}`} strokeWidth={1.5} />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9 text-destructive" onClick={() => del(cv.id)} data-testid={`delete-cv-${cv.id}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="pt-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Tailored CVs</h2>
          <p className="text-muted-foreground mt-1 text-sm">CVs generated for specific jobs. Download or remove them here.</p>
        </div>
        {tailored.length > 1 && (
          <Select value={tailoredSort} onValueChange={setTailoredSort}>
            <SelectTrigger className="w-48" data-testid="tailored-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Newest first</SelectItem>
              <SelectItem value="match">Highest match first</SelectItem>
              <SelectItem value="company">Company (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tailored-cvs-section">
        {tailored.length === 0 && <p className="text-sm text-muted-foreground">No tailored CVs yet. Generate one from the Tailor CV page.</p>}
        {sortedTailored.map((t) => (
          <Card key={t.id} className="border-zinc-200 shadow-sm" data-testid={`tailored-cv-card-${t.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-accent/10 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{t.job_title || "Untitled"}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{t.company || "—"}</p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString()}
                      {t.tailoring_mode ? <span className="capitalize"> · {t.tailoring_mode}</span> : null}
                    </p>
                    {t.alignment && typeof t.alignment.score === "number" && (
                      <Badge
                        className={`gap-1 ${t.alignment.score >= (t.alignment.target_low ?? 0) ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}`}
                        data-testid={`tailored-cv-match-${t.id}`}
                      >
                        {t.alignment.score}% match
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <a href={`${API}/tailored-cvs/${t.id}/download`} target="_blank" rel="noreferrer" className="flex-1" data-testid={`download-tailored-cv-${t.id}`}>
                  <Button variant="outline" size="sm" className="w-full active:scale-[0.98] transition-transform">
                    <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Download PDF
                  </Button>
                </a>
                <ConfirmDelete
                  onConfirm={() => delTailored(t.id)}
                  triggerTestId={`delete-tailored-cv-${t.id}`}
                  confirmTestId={`confirm-delete-tailored-cv-${t.id}`}
                  title="Delete this tailored CV?"
                  description={`"${t.job_title || "Untitled"} · ${t.company || "—"}" and its cover letter will be permanently removed.`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
